import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    LinearProgress,
    Alert,
    Stack,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { CloudUpload, CheckCircle, ErrorOutline, PlayArrow } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import GenericPage from '../../Commun/components/GenericPage';
import { blobticService } from '../../Commun/services/blobticService';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

interface ImportProgress {
    stepName: string;
    total: number;
    current: number;
    status: 'idle' | 'running' | 'success' | 'failed';
}

const MigrationExcel: React.FC = () => {
    const { t } = useLanguage();
    
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [sheetsData, setSheetsData] = useState<any>({});
    const [steps, setSteps] = useState<ImportProgress[]>([
        { stepName: '1. Extraction des listes de référence (Lookups)', total: 0, current: 0, status: 'idle' },
        { stepName: '2. Importation des Conducteurs', total: 0, current: 0, status: 'idle' },
        { stepName: '3. Importation des Véhicules', total: 0, current: 0, status: 'idle' },
        { stepName: '4. Importation des Cartes Carburant', total: 0, current: 0, status: 'idle' },
        { stepName: '5. Importation des Transactions de Carburant', total: 0, current: 0, status: 'idle' },
        { stepName: '6. Importation de l\'Historique de Kilométrage', total: 0, current: 0, status: 'idle' },
        { stepName: '7. Importation des Fiches de Maintenance', total: 0, current: 0, status: 'idle' },
        { stepName: '8. Importation des Documents Véhicule', total: 0, current: 0, status: 'idle' }
    ]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const runStepAction = async (stepIndex: number, action: () => Promise<void>) => {
        setSteps(prev => {
            const next = [...prev];
            next[stepIndex].status = 'running';
            return next;
        });
        try {
            await action();
            setSteps(prev => {
                const next = [...prev];
                next[stepIndex].status = 'success';
                next[stepIndex].current = next[stepIndex].total;
                return next;
            });
        } catch (err) {
            console.error(`Step ${stepIndex} failed:`, err);
            setSteps(prev => {
                const next = [...prev];
                next[stepIndex].status = 'failed';
                return next;
            });
            throw err;
        }
    };

    const updateStepProgress = (stepIndex: number, current: number, total: number) => {
        setSteps(prev => {
            const next = [...prev];
            next[stepIndex].current = current;
            next[stepIndex].total = total;
            return next;
        });
    };

    const handleUploadAndMigrate = async () => {
        if (!file) return;
        setUploading(true);
        
        // Reset steps status
        setSteps(prev => prev.map(s => ({ ...s, status: 'idle', current: 0, total: 0 })));

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                
                const dataSheets: any = {};
                workbook.SheetNames.forEach(sheetName => {
                    const sheet = workbook.Sheets[sheetName];
                    dataSheets[sheetName] = XLSX.utils.sheet_to_json(sheet);
                });
                
                setSheetsData(dataSheets);
                await startMigrationProcess(dataSheets);
            } catch (err) {
                console.error("Migration error:", err);
                blobticMessage.error("Erreur d'importation", "Impossible de lire le fichier Excel.");
                setUploading(false);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const startMigrationProcess = async (data: any) => {
        try {
            const lookupIds: any = {
                proprietaires: {},
                fuelTypes: {},
                affectations: {},
                docTypes: {},
                mntTypes: {}
            };
            
            const driverMap: any = {}; // maps name/id to DRV_IDF
            const vehicleMap: any = {}; // maps registration to VEH_IDF
            const cardMap: any = {}; // maps card code/id to FCD_IDF

            // --- STEP 1: LOOKUPS ---
            await runStepAction(0, async () => {
                // Extract unique lookup entries
                const rawVehicles = data['Vehicules'] || [];
                const rawUsers = data['Utilisateurs'] || [];
                const rawMaint = data['Maintenance'] || [];
                const rawDocs = data['DocumentsVehicule'] || [];

                const props = Array.from(new Set(rawVehicles.map((v: any) => v['Propriétaire']).filter(Boolean)));
                const fuelTypes = Array.from(new Set(rawVehicles.map((v: any) => v['Carburant']).filter(Boolean)));
                const affs = Array.from(new Set(rawVehicles.map((v: any) => v['Affectation']).filter(Boolean)));
                const docTypes = Array.from(new Set(rawDocs.map((d: any) => d['Type document']).filter(Boolean)));
                const mntTypes = Array.from(new Set(rawMaint.map((m: any) => m['Type']).filter(Boolean)));

                const totalLookups = props.length + fuelTypes.length + affs.length + docTypes.length + mntTypes.length;
                updateStepProgress(0, 0, totalLookups);
                let currentLookup = 0;

                // Owner lookups
                for (const p of props) {
                    const res = await blobticService.executeZoomRequest({
                        ZoomKey: 'Blobtic_Parc',
                        Action: { OKey: 'Blobtic_Parc,parc_proprietaire,Insert', PRP_CODE: String(p).substring(0, 12).toUpperCase(), PRP_LIB: String(p) } as any
                    });
                    if (res.success && res.data) {
                        lookupIds.proprietaires[p as string] = (res.data as any).PRP_IDF;
                    }
                    currentLookup++;
                    updateStepProgress(0, currentLookup, totalLookups);
                }

                // Fuel lookups
                for (const f of fuelTypes) {
                    const res = await blobticService.executeZoomRequest({
                        ZoomKey: 'Blobtic_Parc',
                        Action: { OKey: 'Blobtic_Parc,parc_fuel_type,Insert', FTP_CODE: String(f).substring(0, 12).toUpperCase(), FTP_LIB: String(f) } as any
                    });
                    if (res.success && res.data) {
                        lookupIds.fuelTypes[f as string] = (res.data as any).FTP_IDF;
                    }
                    currentLookup++;
                    updateStepProgress(0, currentLookup, totalLookups);
                }

                // Affectation lookups
                for (const a of affs) {
                    const res = await blobticService.executeZoomRequest({
                        ZoomKey: 'Blobtic_Parc',
                        Action: { OKey: 'Blobtic_Parc,parc_affectation,Insert', AFF_CODE: String(a).substring(0, 12).toUpperCase(), AFF_LIB: String(a) } as any
                    });
                    if (res.success && res.data) {
                        lookupIds.affectations[a as string] = (res.data as any).AFF_IDF;
                    }
                    currentLookup++;
                    updateStepProgress(0, currentLookup, totalLookups);
                }

                // Doc Type lookups
                for (const d of docTypes) {
                    const res = await blobticService.executeZoomRequest({
                        ZoomKey: 'Blobtic_Parc',
                        Action: { OKey: 'Blobtic_Parc,parc_doc_type,Insert', DCT_CODE: String(d).substring(0, 12).toUpperCase(), DCT_LIB: String(d) } as any
                    });
                    if (res.success && res.data) {
                        lookupIds.docTypes[d as string] = (res.data as any).DCT_IDF;
                    }
                    currentLookup++;
                    updateStepProgress(0, currentLookup, totalLookups);
                }

                // Mnt Type lookups
                for (const m of mntTypes) {
                    const res = await blobticService.executeZoomRequest({
                        ZoomKey: 'Blobtic_Parc',
                        Action: { OKey: 'Blobtic_Parc,parc_mnt_type,Insert', MTP_CODE: String(m).substring(0, 12).toUpperCase(), MTP_LIB: String(m) } as any
                    });
                    if (res.success && res.data) {
                        lookupIds.mntTypes[m as string] = (res.data as any).MTP_IDF;
                    }
                    currentLookup++;
                    updateStepProgress(0, currentLookup, totalLookups);
                }
            });

            // --- STEP 2: DRIVERS ---
            await runStepAction(1, async () => {
                const rawUsers = data['Utilisateurs'] || [];
                updateStepProgress(1, 0, rawUsers.length);

                for (let i = 0; i < rawUsers.length; i++) {
                    const u = rawUsers[i];
                    const names = String(u['Nom'] || '').split(' ');
                    const firstName = names[0] || 'Conducteur';
                    const lastName = names.slice(1).join(' ') || 'OXXO';
                    const key = u['ID Conducteur'] || u['Nom'];

                    const affId = lookupIds.affectations[u['Direction']] || undefined;

                    const res = await blobticService.executeZoomRequest({
                        ZoomKey: 'Blobtic_Parc',
                        Action: {
                            OKey: 'Blobtic_Parc,parc_driver,Insert',
                            DRV_FIRSTNAME: firstName,
                            DRV_LASTNAME: lastName,
                            DRV_LICENSE_NUMBER: String(u['ID Conducteur'] || 'B0000'),
                            DRV_LICENSE_TYPE: 'B',
                            DRV_FUNCTION: u['Fonction'],
                            DRV_TELEGRAM_CHAT_ID: u['Chat ID Telegram'] ? String(u['Chat ID Telegram']) : undefined,
                            AFF_IDF: affId
                        } as any
                    });

                    if (res.success && res.data) {
                        driverMap[key] = (res.data as any).DRV_IDF;
                    }
                    updateStepProgress(1, i + 1, rawUsers.length);
                }
            });

            // --- STEP 3: VEHICLES ---
            await runStepAction(2, async () => {
                const rawVehicles = data['Vehicules'] || [];
                updateStepProgress(2, 0, rawVehicles.length);

                for (let i = 0; i < rawVehicles.length; i++) {
                    const v = rawVehicles[i];
                    const brandModel = String(v['Marque / Modèle'] || '').split(' ');
                    const brand = brandModel[0] || 'Véhicule';
                    const model = brandModel.slice(1).join(' ') || 'Standard';

                    const ftpId = lookupIds.fuelTypes[v['Carburant']] || undefined;
                    const prpId = lookupIds.proprietaires[v['Propriétaire']] || undefined;
                    const affId = lookupIds.affectations[v['Affectation']] || undefined;

                    const res = await blobticService.executeZoomRequest({
                        ZoomKey: 'Blobtic_Parc',
                        Action: {
                            OKey: 'Blobtic_Parc,parc_vehicle,Insert',
                            VEH_REGISTRATION: String(v['Immat.'] || '00000'),
                            VEH_BRAND: brand,
                            VEH_MODEL: model,
                            VEH_YEAR: Number(v['Année']) || new Date().getFullYear(),
                            VEH_MILEAGE: Number(v['Km total']) || 0,
                            VEH_STATUS: v['Statut'] === 'Actif' ? 'active' : 'maintenance',
                            FTP_IDF: ftpId,
                            PRP_IDF: prpId,
                            AFF_IDF: affId
                        } as any
                    });

                    if (res.success && res.data) {
                        vehicleMap[v['Immat.']] = (res.data as any).VEH_IDF;
                    }
                    updateStepProgress(2, i + 1, rawVehicles.length);
                }
            });

            // --- STEP 4: CARDS ---
            await runStepAction(3, async () => {
                const rawCards = data['CartesCarburant'] || [];
                updateStepProgress(3, 0, rawCards.length);

                for (let i = 0; i < rawCards.length; i++) {
                    const c = rawCards[i];
                    const vehId = vehicleMap[c['Immatriculation']] || undefined;
                    const drvId = driverMap[c['Conducteur titulaire']] || undefined;

                    const res = await blobticService.executeZoomRequest({
                        ZoomKey: 'Blobtic_Parc',
                        Action: {
                            OKey: 'Blobtic_Parc,parc_fuel_card,Insert',
                            FCD_NUMBER: String(c['N° Carte'] || c['ID Catre']),
                            VEH_IDF: vehId,
                            DRV_IDF: drvId,
                            FCD_COUT_GLOBAL: Number(c['Coût Global (DA)']) || 0,
                            FCD_PRIX_LITRE: Number(c['Prix/L (DA)']) || 130
                        } as any
                    });

                    if (res.success && res.data) {
                        cardMap[c['ID Catre']] = (res.data as any).FCD_IDF;
                    }
                    updateStepProgress(3, i + 1, rawCards.length);
                }
            });

            // --- STEP 5: FUEL TXS ---
            await runStepAction(4, async () => {
                const rawFuelTxs = data['Carburant'] || [];
                updateStepProgress(4, 0, rawFuelTxs.length);

                for (let i = 0; i < rawFuelTxs.length; i++) {
                    const f = rawFuelTxs[i];
                    const vehId = vehicleMap[f['Véhicule']] || undefined;
                    const cardId = cardMap[f['ID Catre']] || undefined;
                    const drvId = driverMap[f['Titulaire / Conducteur']] || undefined;

                    // Parse serial excel date or standard date
                    let txDate = new Date();
                    if (typeof f['Date'] === 'number') {
                        // Excel serial date representation
                        txDate = new Date((f['Date'] - 25569) * 86400 * 1000);
                    } else if (f['Date']) {
                        txDate = new Date(f['Date']);
                    }

                    await blobticService.executeZoomRequest({
                        ZoomKey: 'Blobtic_Parc',
                        Action: {
                            OKey: 'Blobtic_Parc,parc_fuel_transaction,Insert',
                            VEH_IDF: vehId,
                            FCD_IDF: cardId,
                            DRV_IDF: drvId,
                            FTR_DATE: txDate.toISOString(),
                            FTR_LITRES: Number(f['Litres']) || 0,
                            FTR_COUT: Number(f['Montant (DA)']) || 0,
                            FTR_STATION: f['Station'] || 'Station OXXO',
                            FTR_PRODUCT: f['Produit'] || 'Gazole',
                            FTR_PRIX_LITRE: Number(f['Prix/L (DA)']) || 130
                        } as any
                    });
                    updateStepProgress(4, i + 1, rawFuelTxs.length);
                }
            });

            // --- STEP 6: MILEAGE ---
            await runStepAction(5, async () => {
                const rawMileage = data['Kilometrage'] || [];
                updateStepProgress(5, 0, rawMileage.length);

                for (let i = 0; i < rawMileage.length; i++) {
                    const km = rawMileage[i];
                    const vehId = vehicleMap[km['Véhicule']] || undefined;
                    const drvId = driverMap[km['Conducteur']] || driverMap[km['ID Conducteur']] || undefined;

                    let dDeb = new Date();
                    let dFin = new Date();
                    if (typeof km['Date Début'] === 'number') {
                        dDeb = new Date((km['Date Début'] - 25569) * 86400 * 1000);
                    }
                    if (typeof km['Date Fin'] === 'number') {
                        dFin = new Date((km['Date Fin'] - 25569) * 86400 * 1000);
                    }

                    await blobticService.executeZoomRequest({
                        ZoomKey: 'Blobtic_Parc',
                        Action: {
                            OKey: 'Blobtic_Parc,parc_kilometrage,Insert',
                            VEH_IDF: vehId,
                            DRV_IDF: drvId,
                            KLM_DATE_DEBUT: dDeb.toISOString(),
                            KLM_DATE_FIN: dFin.toISOString(),
                            KLM_KM_DEBUT: Number(km['Km Début']) || 0,
                            KLM_KM_FIN: Number(km['Km Fin']) || 0
                        } as any
                    });
                    updateStepProgress(5, i + 1, rawMileage.length);
                }
            });

            // --- STEP 7: MAINTENANCE ---
            await runStepAction(6, async () => {
                const rawMaint = data['Maintenance'] || [];
                updateStepProgress(6, 0, rawMaint.length);

                for (let i = 0; i < rawMaint.length; i++) {
                    const m = rawMaint[i];
                    const vehId = vehicleMap[m['Véhicule']] || undefined;
                    const mtpId = lookupIds.mntTypes[m['Type']] || undefined;

                    let maintDate = new Date();
                    if (typeof m['Date'] === 'number') {
                        maintDate = new Date((m['Date'] - 25569) * 86400 * 1000);
                    }

                    let dDebP = undefined;
                    let dFinP = undefined;
                    if (typeof m['Date début panne'] === 'number') {
                        dDebP = new Date((m['Date début panne'] - 25569) * 86400 * 1000).toISOString();
                    }
                    if (typeof m['Date fin panne'] === 'number') {
                        dFinP = new Date((m['Date fin panne'] - 25569) * 86400 * 1000).toISOString();
                    }

                    await blobticService.executeZoomRequest({
                        ZoomKey: 'Blobtic_Parc',
                        Action: {
                            OKey: 'Blobtic_Parc,parc_maintenance,Insert',
                            VEH_IDF: vehId,
                            MTP_IDF: mtpId,
                            MNT_CATEGORY: 'Corrective',
                            MNT_TITLE: m['Type'] || 'Vidange',
                            MNT_DESCRIPTION: m['Description'] || '',
                            MNT_SCHEDULED_DATE: maintDate.toISOString(),
                            MNT_START_DATE: maintDate.toISOString(),
                            MNT_END_DATE: maintDate.toISOString(),
                            MNT_STATUS: 'completed',
                            MNT_PRIORITY: 'medium',
                            MNT_COST: Number(m['Coût (DA)']) || 0,
                            MNT_TYPE_PANNE: m['Type panne'] || undefined,
                            MNT_DATE_DEBUT_PANNE: dDebP,
                            MNT_DATE_FIN_PANNE: dFinP,
                            MNT_IMMOBILISATION_JOURS: Number(m['Immobilisation (jours)']) || 0
                        } as any
                    });
                    updateStepProgress(6, i + 1, rawMaint.length);
                }
            });

            // --- STEP 8: DOCUMENTS ---
            await runStepAction(7, async () => {
                const rawDocs = data['DocumentsVehicule'] || [];
                updateStepProgress(7, 0, rawDocs.length);

                for (let i = 0; i < rawDocs.length; i++) {
                    const d = rawDocs[i];
                    const vehId = vehicleMap[d['Véhicule']] || undefined;
                    const dctId = lookupIds.docTypes[d['Type document']] || undefined;

                    let dEmis = new Date();
                    let dExp = new Date();
                    if (typeof d['Date émission'] === 'number') {
                        dEmis = new Date((d['Date émission'] - 25569) * 86400 * 1000);
                    }
                    if (typeof d['Date expiration'] === 'number') {
                        dExp = new Date((d['Date expiration'] - 25569) * 86400 * 1000);
                    }

                    await blobticService.executeZoomRequest({
                        ZoomKey: 'Blobtic_Parc',
                        Action: {
                            OKey: 'Blobtic_Parc,parc_document,Insert',
                            VEH_IDF: vehId,
                            DCT_IDF: dctId,
                            DOC_TITLE: d['Type document'] || 'Assurance',
                            DOC_EMISSION_DATE: dEmis.toISOString(),
                            DOC_EXPIRY_DATE: dExp.toISOString(),
                            DOC_ALERTE_JOURS: Number(d['Alerte (jours)']) || 30,
                            DOC_REMARQUE: d['Remarque'] || ''
                        } as any
                    });
                    updateStepProgress(7, i + 1, rawDocs.length);
                }
            });

            blobticMessage.success("Migration Terminée", "Toutes les tables ont été importées avec succès.");
        } catch (err) {
            console.error("Migration halted on error:", err);
            blobticMessage.error("Échec de la Migration", "Le processus d'importation a été interrompu.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <GenericPage title="Migration de Données (Excel FleetOS)" footerEnabled={false}>
            <Stack spacing={3}>
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Sélectionner le fichier Excel</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Sélectionnez la base de données `FleetOS_Base des données.xlsx` pour recharger automatiquement toute l'organisation de votre parc (véhicules, conducteurs, cartes, factures carburant, maintenance).
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUpload />}
                            >
                                Choisir le fichier
                                <input
                                    type="file"
                                    hidden
                                    accept=".xlsx, .xls"
                                    onChange={handleFileChange}
                                />
                            </Button>
                            {file && (
                                <Typography variant="subtitle2" color="primary">
                                    {file.name} ({Math.round(file.size / 1024)} KB)
                                </Typography>
                            )}
                        </Box>

                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<PlayArrow />}
                            sx={{ mt: 3 }}
                            disabled={!file || uploading}
                            onClick={handleUploadAndMigrate}
                        >
                            Lancer la migration
                        </Button>
                    </CardContent>
                </Card>

                {/* Progress Indicators */}
                {(uploading || steps.some(s => s.status !== 'idle')) && (
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Progression de l'Importation</Typography>
                            <List>
                                {steps.map((step, idx) => (
                                    <ListItem key={idx} sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {step.status === 'success' && <CheckCircle color="success" />}
                                                {step.status === 'failed' && <ErrorOutline color="error" />}
                                                {step.status === 'running' && <LinearProgress sx={{ width: 20 }} />}
                                                <ListItemText primary={step.stepName} />
                                            </Box>
                                            {step.total > 0 && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {step.current} / {step.total}
                                                </Typography>
                                            )}
                                        </Box>
                                        {step.status === 'running' && (
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={step.total > 0 ? (step.current / step.total) * 100 : 0} 
                                                sx={{ width: '100%', mt: 0.5 }}
                                            />
                                        )}
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                )}
            </Stack>
        </GenericPage>
    );
};

export default MigrationExcel;
